import React, { useCallback, useContext } from 'react';

import { Box, Menu, MenuItem } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import SkeletonLoader from 'components/SkeletonLoader';
import { StyledAvatar } from 'components/avatars';
import isAfter from 'date-fns/isAfter';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  StyledBox,
  StyledButton,
  StyledIconButton,
  StyledPaper,
  StyledTooltip,
  StyledTypography,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
} from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { convertDateToString11, convertDateToString7 } from 'constants/helper';
import { ChevronDownIcon, DotsIcon } from 'constants/icons';
import theme from 'constants/theme';

import { CheckInTime } from './CheckInTime';
import { InstanceList } from './InstanceList';
import { OneOnOneInstanceContext } from './InstancePageContext';
import { MeetingMenu } from './MeetingMenu';

const Header = ({
  instanceDetails,
  otherEmployee,
  isManager,
  isSubmitted = false,
  handleMarkAsComplete = () => {},
  handleReOpen,
  isLoading = false,
  isDisabled = false,
}) => {
  const history = useHistory();
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const { toggleCancelMeetingModal, dateTimeChangeHandler } = useContext(contextToUse);

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const isActiveTask = instanceDetails?.active;

  const { coloredStartDate, coloredDueDate } = instanceDetails;

  // THE USER CAN START A FUTURE MEETING BUT CAN ONLY MARK IT COMPLETE AFTER THE START DATE/TIME
  const canMarkAsComplete = !isAfter(new Date(coloredStartDate.timeStamp), new Date(Date.now()));

  const isEditable =
    isActiveTask &&
    (instanceDetails?.checkInState === ONE_ON_ONE_STATUSES.ACTIVE ||
      instanceDetails?.checkInState === ONE_ON_ONE_STATUSES.NOT_STARTED);

  if (!instanceDetails) return <SkeletonLoader />;
  return (
    <StyledPaper
      elevation={0}
      showBorder
      padding="1rem"
      bottomGutter={2}
      flexcontainer
      alignitems="center"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'white',
        boxShadow: '3px 4px 5px rgba(0, 0, 0, 0.12)',
      }}
    >
      <StyledIconButton rightGutter={1} onClick={goBack} size="header">
        <ArrowBack />
      </StyledIconButton>
      <StyledAvatar
        src={otherEmployee?.profilePhotoSrc}
        size="medium"
        variant="circular"
        leftGutter={2}
        rightGutter={3}
      />
      <VerticalFlexContainer>
        <StyledTypography variant="body2" weight={600} bottomGutter={1}>
          {otherEmployee?.fullName}
        </StyledTypography>
        <VerticallyAlignedFlexContainer>
          <StyledCheckInTime
            startDateTime={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
            endDateTime={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
            onDateTimeChange={dateTimeChangeHandler}
            editable={isEditable && !isDisabled}
            isCompleted={
              instanceDetails?.checkInState === ONE_ON_ONE_STATUSES.CANCELLED ||
              instanceDetails?.checkInState === ONE_ON_ONE_STATUSES.ACKNOWLEDGED
            }
          />
          <InstanceList
            isManager={isManager}
            isActiveTask={isActiveTask}
            currentInstanceId={instanceDetails?.id}
            // isDisabled={isDisabled}
          />
        </VerticallyAlignedFlexContainer>
      </VerticalFlexContainer>

      <Box flexGrow={1} display="flex" justifyContent="flex-end" alignItems="center">
        {instanceDetails?.checkInState === ONE_ON_ONE_STATUSES.ACTIVE && (
          <>
            {canMarkAsComplete ? (
              <StyledBox>
                <CustomStyledButton
                  fullWidth
                  variant="contained"
                  customColor={theme.palette.primary.main}
                  fontColor="white"
                  onClick={handleMarkAsComplete}
                  disabled={isLoading || isDisabled}
                  disableElevation
                >
                  Mark as Complete
                </CustomStyledButton>
              </StyledBox>
            ) : (
              <StyledTooltip
                title={`You can mark this meeting as complete after the scheduled time -  ${convertDateToString11(
                  coloredStartDate?.timeStamp
                )}  at ${convertDateToString7(coloredStartDate?.timeStamp, true)}`}
              >
                <StyledBox>
                  <CustomStyledButton
                    fullWidth
                    variant="contained"
                    customColor={theme.palette.primary.main}
                    fontColor="white"
                    onClick={handleMarkAsComplete}
                    disabled
                    disableElevation
                  >
                    Mark as Complete
                  </CustomStyledButton>
                </StyledBox>
              </StyledTooltip>
            )}
          </>
        )}
        <MeetingMenu
          startDateTime={coloredStartDate?.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
          endDateTime={coloredDueDate?.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
          onDateTimeChange={dateTimeChangeHandler}
          meetingStatus={instanceDetails?.checkInState}
          handleReOpen={handleReOpen}
          toggleCancelMeetingModal={toggleCancelMeetingModal}
          isDisabled={isDisabled}
        />
      </Box>
    </StyledPaper>
  );
};

export const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

export const StyledMenu = styled(Menu)`
  &.MuiPopover-root ul {
    padding: 0;
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  padding: 0.75rem 1rem;
`;

const StyledCheckInTime = styled(CheckInTime)`
  margin-right: 1.5rem;

  @media (max-width: 1439px) {
    margin-right: 0.5rem;
    width: 80px;
  }
`;

const CustomStyledButton = styled(StyledButton)`
  line-height: 1;
  padding: 13px;
  width: 160px;

  @media (max-width: 1439px) {
    width: 135px;
  }
`;

export const StyledChevronDownIcon = styled(ChevronDownIcon)`
  svg {
    margin-top: 2px;
  }
`;

export default Header;
